import { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import apiService from '../services/api'
import { ASSISTANTS } from '../data/assistants'

// Fallback for browsers without crypto.randomUUID (e.g., Safari < 15.4)
const generateUUID = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  // Fallback implementation
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

const IdeaChatModal = ({ isOpen, onClose, onIdeaCreated, isWelcome = false, initialIdeaDescription = '', initialTeamMembers = [] }) => {
  const [messages, setMessages] = useState([])
  const [currentInput, setCurrentInput] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isTyping, setIsTyping] = useState(false)
  const [conversationStarted, setConversationStarted] = useState(false)
  const [showWelcomeChoice, setShowWelcomeChoice] = useState(false)
  const [welcomeChoiceType, setWelcomeChoiceType] = useState('initial')
  const [showCloseButton, setShowCloseButton] = useState(false)
  const [ideaId] = useState(() => generateUUID()) // Pre-generate UUID for X-Ray tracking
  const [showMobileNav, setShowMobileNav] = useState(false)
  const messagesEndRef = useRef(null)
  const hasInitialized = useRef(false)

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages, isTyping])

  useEffect(() => {
    if (!isOpen) {
      hasInitialized.current = false
      setIsSubmitting(false)
      setIsTyping(false)
      setConversationStarted(false)
      setShowWelcomeChoice(false)
      setShowCloseButton(false)
      return
    }
    
    if (isOpen && !hasInitialized.current) {
      hasInitialized.current = true
      
      setMessages([])
      setCurrentInput('')
      setConversationStarted(false)
      setShowWelcomeChoice(false)
      setShowCloseButton(false)
      
      if (isWelcome) {
        setIsTyping(true)
        setTimeout(() => {
          setMessages([{ type: 'bot', text: "Hi, I'm the Opptora idea assistant, and **I'll help you get started.**" }])
          setIsTyping(false)
          
          setTimeout(() => {
            setIsTyping(true)
            setTimeout(() => {
              setMessages(prev => [...prev, { type: 'bot', text: "We can either **dive straight into creating an idea**, I can **tell you more about how Opptora works**, or you can **explore on your own**. What do you fancy?" }])
              setIsTyping(false)
              setWelcomeChoiceType('initial')
              setShowWelcomeChoice(true)
            }, 1200)
          }, 1500)
        }, 1200)
      } else {
        const fetchInitialMessage = async () => {
          if (initialIdeaDescription) {
            setMessages([{
              type: 'bot',
              text: "Let's begin - Please can you tell me about your idea?"
            }])
            setConversationStarted(true)

            setTimeout(async () => {
              setMessages(prev => [...prev, { type: 'user', text: initialIdeaDescription }])
              setIsTyping(true)

              try {
                const response = await apiService.sendCreationChat(ideaId, [
                  { role: 'assistant', content: "Let's begin - Please can you tell me about your idea?" },
                  { role: 'user', content: initialIdeaDescription }
                ])
                setMessages(prev => [...prev, { type: 'bot', text: response.message }])
              } catch (error) {
                console.error('Failed to get response:', error)
                setMessages(prev => [...prev, {
                  type: 'bot',
                  text: "Thanks for sharing that! Let me ask you some questions to better understand your idea."
                }])
              } finally {
                setIsTyping(false)
              }
            }, 800)
          } else {
            setIsTyping(true)
            try {
              const response = await apiService.sendCreationChat(ideaId, [
                { role: 'user', content: 'Hello, I want to create a new idea.' }
              ])
              setMessages([
                { type: 'bot', text: response.message }
              ])
            } catch (error) {
              console.error('Failed to get initial message:', error)
              setMessages([{
                type: 'bot',
                text: "Let's begin - Please can you tell me about your idea?"
              }])
            } finally {
              setIsTyping(false)
              setConversationStarted(true)
            }
          }
        }
        
        fetchInitialMessage()
      }
    }
  }, [isOpen, isWelcome, initialIdeaDescription])

  const handleWelcomeChoice = (choice) => {
    setShowWelcomeChoice(false)
    
    if (choice === 'create') {
      setMessages(prev => [...prev, { type: 'user', text: 'Create my first idea' }])
      setTimeout(() => {
        const fetchMessage = async () => {
          setIsTyping(true)
          try {
            const response = await apiService.sendCreationChat(ideaId, [
              { role: 'user', content: 'I want to create a new idea.' }
            ])
            // For welcome modal, use a shortened greeting to avoid duplication
            let aiMessage = response.message
            if (isWelcome && aiMessage.includes("I'm the Idea Creation Assistant")) {
              aiMessage = aiMessage.replace(
                "Hi! I'm the Idea Creation Assistant, and I'll help you capture your concept through a natural conversation. Once I have enough detail to complete all 7 fields of your idea, I'll submit it to our triage agents for review.",
                "Right, I'll help you capture your concept through a natural conversation. Once I have enough detail to complete all 7 fields of your idea, I'll submit it to our triage agents for review."
              )
            }
            setMessages(prev => [...prev, { type: 'bot', text: aiMessage }])
            setConversationStarted(true)
          } catch (error) {
            console.error('Failed to start creation:', error)
            setMessages(prev => [...prev, { 
              type: 'bot', 
              text: "Right, let's begin - Please can you tell me about your idea?"
            }])
            setConversationStarted(true)
          } finally {
            setIsTyping(false)
          }
        }
        fetchMessage()
      }, 800)
    } else if (choice === 'learn') {
      setMessages(prev => [...prev, { type: 'user', text: 'Tell me more' }])
      setTimeout(() => {
        setIsTyping(true)
        setTimeout(() => {
          setMessages(prev => [...prev, { type: 'bot', text: "**Opptora starts with your idea.** When you're ready, I'll ask you questions to help you structure your idea, and then our team of AI Agents will review and share feedback on it.\n\nYour idea will flow through these steps:\n• **Step 1:** Our AI Triage Agents assess if your idea is ready for evaluation\n• **Steps 2-4:** Our AI Market Analysis agents research demand, competition, and your unique value\n• **Step 5:** Our AI Opportunity agents summarise the strength of your idea and share an overall Opportunity Score\n\nYou'll be able to track progress of your idea on the Dashboard.\n\nYou'll also have access to your idea's **dedicated Ideation Board and Tasks List** to help move your idea forward." }])
          setIsTyping(false)
          setTimeout(() => {
            setIsTyping(true)
            setTimeout(() => {
              setMessages(prev => [...prev, { type: 'bot', text: "**What would you like to do now?** Create your first idea or explore the site on your own?" }])
              setIsTyping(false)
              setWelcomeChoiceType('afterLearn')
              setShowWelcomeChoice(true)
            }, 1200)
          }, 5000)
        }, 1200)
      }, 800)
    } else if (choice === 'explore') {
      setMessages(prev => [...prev, { type: 'user', text: 'Explore on my own' }])
      setTimeout(() => {
        setIsTyping(true)
        setTimeout(() => {
          setMessages(prev => [...prev, { type: 'bot', text: "No problem! Feel free to **explore Opptora at your own pace.** When you're ready to create an idea, just click the **+ New Idea** button." }])
          setIsTyping(false)
          setShowCloseButton(true)
        }, 1200)
      }, 800)
    }
  }

  const handleSubmit = async (e) => {
    if (e) e.preventDefault()
    if (!currentInput.trim() || isSubmitting || isTyping) return

    const userMessage = currentInput.trim()
    setMessages(prev => [...prev, { type: 'user', text: userMessage }])
    setCurrentInput('')
    setIsTyping(true)

    try {
      const chatMessages = messages
        .concat([{ type: 'user', text: userMessage }])
        .map(msg => ({
          role: msg.type === 'user' ? 'user' : 'assistant',
          content: msg.text
        }))

      console.log('Sending chat messages:', JSON.stringify(chatMessages, null, 2))
      const response = await apiService.sendCreationChat(ideaId, chatMessages)
      
      try {
        // Try to extract JSON from the response, even if there's text before/after it
        let jsonMatch = response.message.match(/\{[\s\S]*\}/)
        if (jsonMatch) {
          const completion = JSON.parse(jsonMatch[0])
          if (completion.action === 'complete' && completion.idea_data) {
            await handleAutoSubmit(completion.idea_data, completion.message)
            return
          }
        }
      } catch (parseErr) {
        // Not valid JSON, continue with regular conversation
        console.log('Could not parse completion JSON:', parseErr)
      }
      
      setMessages(prev => [...prev, { 
        type: 'bot', 
        text: response.message 
      }])
    } catch (error) {
      console.error('Failed to get chat response:', error)
      let errorMessage = "I'm having trouble responding right now. Please try again."
      
      // Check if it's a rate limit error
      if (error.message && error.message.includes('429')) {
        errorMessage = "I'm experiencing high demand right now. Please wait a moment and try sending your message again."
      } else if (error.message && error.message.includes('high demand')) {
        errorMessage = error.message
      }
      
      setMessages(prev => [...prev, { 
        type: 'bot', 
        text: errorMessage 
      }])
    } finally {
      setIsTyping(false)
    }
  }

  const handleAutoSubmit = async (ideaData, aiMessage) => {
    setIsSubmitting(true)
    setIsTyping(true)
    
    try {
      setMessages(prev => [...prev, {
        type: 'bot',
        text: aiMessage
      }])

      const ideaWithTeam = {
        ...ideaData,
        idea_id: ideaId,
        team_members: initialTeamMembers.length > 0 ? initialTeamMembers : undefined
      }

      console.log('Creating idea with team_members:', initialTeamMembers, 'ideaWithTeam:', ideaWithTeam)
      const response = await apiService.createIdea(ideaWithTeam)
      console.log('Idea created successfully:', response)
      
      if (response.idea_id) {
        setMessages(prev => [...prev, { type: 'bot', text: '✨ Your idea has been created successfully!' }])
        setIsTyping(false)
        setTimeout(() => {
          console.log('Auto-closing modal after idea creation, idea_id:', response.idea_id)
          onIdeaCreated(response.idea_id)
          handleClose()
        }, 1500)
      } else {
        console.warn('Idea creation response missing idea_id:', response)
        setIsTyping(false)
        setIsSubmitting(false)
      }
    } catch (error) {
      console.error('Failed to submit idea:', error)
      setIsTyping(false)
      setMessages(prev => [...prev, { 
        type: 'bot', 
        text: "I encountered an error submitting your idea. Please try again." 
      }])
      setIsSubmitting(false)
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit()
    }
  }

  const handleClose = () => {
    setMessages([])
    setCurrentInput('')
    setConversationStarted(false)
    setShowWelcomeChoice(false)
    setShowCloseButton(false)
    onClose()
  }

  if (!isOpen) return null

  // Helper to render message text with bold formatting
  const renderMessageText = (text) => {
    return text.split(/(\*\*.*?\*\*)/).map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i}>{part.slice(2, -2)}</strong>
      }
      return part
    })
  }

  // Mobile: Full-screen with Opptora header and avatar in messages
  // Desktop: Modal with floating character
  return (
    <>
      {/* Mobile Layout - Full screen takeover */}
      <div className="fixed inset-0 z-50 flex flex-col bg-gray-900 sm:hidden">
        {/* Mobile Header Row 1 - Opptora branding + nav menu */}
        <div className="flex-shrink-0 bg-gray-800 border-b border-white/10 relative">
          <div className="flex h-12 items-center justify-between px-4">
            <div className="flex items-center gap-2">
              <img src={`${import.meta.env.BASE_URL}images/logo.png`} alt="Opptora" className="w-6 h-6" />
              <span className="text-white font-semibold text-base">Opptora</span>
            </div>
            {/* Nav menu button */}
            <button
              onClick={() => setShowMobileNav(!showMobileNav)}
              className="text-gray-400 hover:text-white transition-colors p-1"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>

          {/* Mobile Nav Dropdown */}
          {showMobileNav && (
            <div className="absolute top-full right-0 left-0 bg-gray-800 border-b border-white/10 shadow-lg z-50">
              <nav className="py-2">
                <a href="/app" className="block px-4 py-3 text-gray-300 hover:bg-gray-700 hover:text-white transition-colors">
                  Dashboard
                </a>
                <a href="/app" className="block px-4 py-3 text-gray-300 hover:bg-gray-700 hover:text-white transition-colors">
                  My Ideas
                </a>
                <button
                  onClick={handleClose}
                  className="w-full text-left px-4 py-3 text-gray-300 hover:bg-gray-700 hover:text-white transition-colors"
                >
                  Close Chat
                </button>
              </nav>
            </div>
          )}
        </div>

        {/* Mobile Header Row 2 - Chat context + close */}
        <div className="flex-shrink-0 bg-gray-800/50 border-b border-white/10">
          <div className="flex h-11 items-center justify-between px-4">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full overflow-hidden bg-gray-700 flex items-center justify-center">
                <img
                  src={`${import.meta.env.BASE_URL}characters/Orin/1_Default/1_thumbup.png`}
                  alt="Orin"
                  className="w-full h-full object-cover object-top scale-[1.8] translate-y-1.5"
                />
              </div>
              <span className="text-white text-sm font-medium">Chatting with Orin</span>
            </div>
            {!isSubmitting && (
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-white transition-colors p-1"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Mobile Messages - with avatar */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {messages.map((msg, idx) => (
            <div key={idx} className={`flex gap-2 ${msg.type === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
              {msg.type === 'bot' && (
                <div className="w-8 h-8 flex-shrink-0 rounded-full overflow-hidden bg-gray-700 flex items-center justify-center">
                  <img
                    src={`${import.meta.env.BASE_URL}characters/Orin/1_Default/1_thumbup.png`}
                    alt="Orin"
                    className="w-full h-full object-cover object-top scale-[1.8] translate-y-2"
                  />
                </div>
              )}
              <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl ${
                msg.type === 'user'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-800 text-gray-100'
              }`}>
                <div className="text-sm whitespace-pre-wrap break-words leading-relaxed">
                  {renderMessageText(msg.text)}
                </div>
              </div>
            </div>
          ))}
          {isTyping && (
            <div className="flex gap-2">
              <div className="w-8 h-8 flex-shrink-0 rounded-full overflow-hidden bg-gray-700 flex items-center justify-center">
                <img
                  src={`${import.meta.env.BASE_URL}characters/Orin/1_Default/1_thumbup.png`}
                  alt="Orin"
                  className="w-full h-full object-cover object-top scale-[1.8] translate-y-2"
                />
              </div>
              <div className="px-4 py-3 rounded-2xl bg-gray-800">
                <div className="flex gap-1">
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Mobile Welcome Choice Buttons */}
        {showWelcomeChoice && (
          <div className="flex-shrink-0 px-4 py-4 border-t border-white/10 bg-gray-800/50">
            {welcomeChoiceType === 'initial' ? (
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => handleWelcomeChoice('create')}
                  className="w-full px-4 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-medium text-sm"
                >
                  Create an Idea
                </button>
                <button
                  onClick={() => handleWelcomeChoice('learn')}
                  className="w-full px-4 py-3 bg-gray-700 text-white rounded-xl hover:bg-gray-600 transition-colors font-medium text-sm"
                >
                  Tell Me More
                </button>
                <button
                  onClick={() => handleWelcomeChoice('explore')}
                  className="w-full px-4 py-3 bg-gray-700 text-white rounded-xl hover:bg-gray-600 transition-colors font-medium text-sm"
                >
                  Explore on My Own
                </button>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => handleWelcomeChoice('create')}
                  className="w-full px-4 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-medium text-sm"
                >
                  Create an Idea
                </button>
                <button
                  onClick={() => handleWelcomeChoice('explore')}
                  className="w-full px-4 py-3 bg-gray-700 text-white rounded-xl hover:bg-gray-600 transition-colors font-medium text-sm"
                >
                  Explore on My Own
                </button>
              </div>
            )}
          </div>
        )}

        {/* Mobile Input */}
        {!isSubmitting && conversationStarted && !showWelcomeChoice && (
          <div className="flex-shrink-0 px-4 py-3 border-t border-white/10 bg-gray-800">
            <form onSubmit={handleSubmit} className="flex gap-2">
              <textarea
                value={currentInput}
                onChange={(e) => setCurrentInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type your message..."
                disabled={isTyping || isSubmitting}
                className="flex-1 px-4 py-3 bg-gray-900/50 border border-gray-600 rounded-xl text-white text-base placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none disabled:opacity-50"
                rows="1"
              />
              <button
                type="submit"
                disabled={!currentInput.trim() || isTyping || isSubmitting}
                className="px-4 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50 flex-shrink-0"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              </button>
            </form>
          </div>
        )}

        {/* Mobile Close Button */}
        {showCloseButton && (
          <div className="flex-shrink-0 px-4 py-4 border-t border-white/10 bg-gray-800">
            <button
              onClick={handleClose}
              className="w-full px-4 py-3 bg-gray-700 text-white rounded-xl hover:bg-gray-600 transition-colors font-medium text-sm"
            >
              Close
            </button>
          </div>
        )}
      </div>

      {/* Desktop Layout - Modal with floating character */}
      <div className="hidden sm:flex fixed inset-0 bg-black/80 items-start justify-center z-50 p-4 overflow-y-auto">
        <div className="rounded-2xl bg-gradient-to-br from-gray-700/80 to-gray-700/80 shadow-none outline -outline-offset-1 outline-white/10 relative overflow-visible max-w-4xl w-full h-[600px] flex flex-col mt-48">
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent pointer-events-none"></div>
          <div className="absolute top-0 left-0 w-96 h-40 bg-gradient-to-br from-blue-900/20 via-transparent to-transparent rounded-tl-2xl pointer-events-none"></div>
          <div className="absolute bottom-0 right-0 w-64 h-32 bg-gradient-to-tl from-blue-900/20 via-transparent to-transparent rounded-br-2xl pointer-events-none"></div>

          {/* Floating character for desktop */}
          {!isWelcome && (
            <div className="absolute -top-48 left-1/2 -translate-x-1/2 w-64 h-64 z-50">
              <img
                src={`${import.meta.env.BASE_URL}characters/Orin/1_Default/1_thumbup.png`}
                alt="Orin"
                className="w-full h-full object-contain drop-shadow-2xl"
              />
            </div>
          )}

          <div className="px-6 py-6 border-b border-white/10 relative z-10">
            <div className="flex items-center justify-between">
              {isWelcome ? (
                <h2 className="font-display text-2xl md:text-3xl font-light tracking-tight text-white flex items-center gap-3">
                  <img src={`${import.meta.env.BASE_URL}images/logo.png`} alt="Opptora" className="w-10 h-10" />
                  Welcome to Opptora!
                </h2>
              ) : (
                <div className="text-center w-full pt-16">
                  <h2 className="text-2xl font-light text-white mb-2" style={{ fontFamily: 'var(--font-handwritten)' }}>
                    Hi, I&apos;m Orin, your Opportunity Analyst
                  </h2>
                  <p className="text-gray-300">
                    {ASSISTANTS.orin.intro}
                  </p>
                </div>
              )}
              {!isSubmitting && (
                <button
                  onClick={handleClose}
                  className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-6 relative z-10 min-h-0">
            <div className="space-y-4 mb-4">
              {messages.map((msg, idx) => (
                <div key={idx} className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] rounded-lg px-4 py-3 ${
                    msg.type === 'user'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-800/80 text-gray-100'
                  }`}>
                    <div className="whitespace-pre-wrap break-words leading-relaxed">
                      {renderMessageText(msg.text)}
                    </div>
                  </div>
                </div>
              ))}
              {isTyping && (
                <div className="flex justify-start">
                  <div className="max-w-[80%] rounded-lg px-4 py-3 bg-gray-800/90 border border-gray-700/50">
                    <div className="flex gap-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>

          {showWelcomeChoice && (
            <div className="px-6 py-6 border-t border-white/10 relative z-10">
              <div className="relative z-10">
                {welcomeChoiceType === 'initial' ? (
                  <div className="flex gap-3">
                    <button
                      onClick={() => handleWelcomeChoice('create')}
                      className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                    >
                      Create an Idea
                    </button>
                    <button
                      onClick={() => handleWelcomeChoice('learn')}
                      className="flex-1 px-4 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors font-medium"
                    >
                      Tell Me More
                    </button>
                    <button
                      onClick={() => handleWelcomeChoice('explore')}
                      className="flex-1 px-4 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors font-medium"
                    >
                      Explore on My Own
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-3">
                    <button
                      onClick={() => handleWelcomeChoice('create')}
                      className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                    >
                      Create an Idea
                    </button>
                    <button
                      onClick={() => handleWelcomeChoice('explore')}
                      className="flex-1 px-4 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors font-medium"
                    >
                      Explore on My Own
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {!isSubmitting && conversationStarted && !showWelcomeChoice && (
            <div className="px-6 py-4 border-t border-white/10 relative z-10 flex-shrink-0">
              <form onSubmit={handleSubmit} className="flex gap-3">
                <textarea
                  value={currentInput}
                  onChange={(e) => setCurrentInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Type your message..."
                  disabled={isTyping || isSubmitting}
                  className="flex-1 px-4 py-3 bg-gray-900/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none disabled:opacity-50 disabled:cursor-not-allowed"
                  rows="1"
                />
                <button
                  type="submit"
                  disabled={!currentInput.trim() || isTyping || isSubmitting}
                  className="px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors duration-200 text-sm font-medium shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Send
                </button>
              </form>
            </div>
          )}

          {showCloseButton && (
            <div className="px-6 py-6 border-t border-white/10 relative z-10 flex-shrink-0">
              <button
                onClick={handleClose}
                className="w-full px-4 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors font-medium"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

IdeaChatModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onIdeaCreated: PropTypes.func.isRequired,
  isWelcome: PropTypes.bool,
  initialIdeaDescription: PropTypes.string,
  initialTeamMembers: PropTypes.arrayOf(PropTypes.string)
}

export default IdeaChatModal
